<script lang="ts" setup>
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import type { Ref } from 'vue';
import type { StaticPageImage } from '~/types/StaticPageList';

const props = defineProps<{ images: StaticPageImage[] }>();

const showPagination = computed(() => props.images?.length > 1);

const gallerySlider: Ref<any> = ref();
const swiper = ref();
const swiperPagination = ref();

onMounted(() => {
  sliderInitialization();
});

function sliderInitialization() {
  gallerySlider.value = new Swiper(swiper.value, {
    autoplay: {
      delay: 5000,
    },
    breakpoints: {
      640: {
        centeredSlides: false,
        slidesOffsetAfter: 12,
        slidesOffsetBefore: 12,
      },
      768: {
        centeredSlides: false,
        slidesOffsetAfter: 12,
        slidesOffsetBefore: 12,
        spaceBetween: 24,
      },
      1024: {
        centeredSlides: false,
        slidesOffsetAfter: 0,
        slidesOffsetBefore: 0,
        spaceBetween: 24,
      },
    },
    centeredSlides: true,
    modules: [Autoplay, Pagination],
    pagination: {
      el: swiperPagination.value,
      type: 'bullets',
    },
    rewind: true,
    slidesPerView: 'auto',
    spaceBetween: 12,
    speed: 500,
  });
}
</script>

<template>
  <div class="constructor-slider">
    <div
      ref="swiper"
      class="swiper-container constructor-slider__container"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(image, index) in images"
          :key="`slide-${index}`"
          class="swiper-slide constructor-slider__slide"
        >
          <AppLink
            v-if="image.properties?.link"
            :title="image.properties?.title"
            :to="image.properties?.link"
            class="constructor-slider__slide-link"
          />
          <picture class="constructor-slider__slide-picture">
            <source
              :srcset="`${image.conversions_webp.thumb_390}, ${image.conversions_webp.thumb_780} 2x`"
              type="image/webp"
            />
            <source
              :srcset="image.conversions.thumb_390"
              type="image/jpeg"
            />
            <img
              :alt="image.properties?.alt"
              :src="image.url"
              class="constructor-slider__slide-img swiper-lazy"
              height="400"
              loading="lazy"
              width="320"
            />
          </picture>
        </div>
      </div>
    </div>
    <div
      v-show="showPagination"
      ref="swiperPagination"
      class="constructor-slider__pagination swiper-pagination"
    />
  </div>
</template>

<style lang="scss" scoped>
.constructor-slider {
  display: flex;
  flex-direction: column;
  width: 100%;

  @include screen-lg {
    @include inner-content;
  }

  &__container {
    overflow: hidden;
  }

  &__slide {
    overflow: hidden;
    width: 82vw;
    max-width: 380px;
    height: 205px;
    border-radius: 12px;

    @include screen-md {
      height: 240px;
    }
  }

  &__slide-link {
    @include image-link;

    z-index: 1;
    border-radius: 12px;
  }

  &__slide-picture {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  &__slide-img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  &__pagination {
    margin-top: 12px;
  }
}
</style>
